/**
 * EduTool Bridge — Multi-context implementation
 *
 * Unified API that works in all 3 contexts:
 *   - KMP WebView (Android JavascriptInterface OR iOS WKScriptMessageHandler)
 *   - Eduverse iframe (window.parent.postMessage)
 *   - Standalone (URL query params only, no host messaging)
 *
 * Usage:
 *   const bridge = new EduToolBridge(manifest);
 *   await bridge.init();
 *   bridge.on('EDUTOOL_CMD', (msg) => { ... });
 *   bridge.send('EDUTOOL_INTERACTION', { action: 'click', target: 'canto-5' });
 */

import type {
  BridgeMessage,
  BridgeMessageHandler,
  BridgeMessageType,
  BridgeUnsubscribe,
  InitPayload,
  Locale,
  SafeAreaInsets,
  Theme,
  ToolManifest,
  UserRole,
} from './bridge-types';
import { detectContext, type BridgeContext } from './bridge-contexts';

interface WindowWithKmp extends Window {
  EduverseBridge?: {
    postMessage: (json: string) => void;
  };
  webkit?: {
    messageHandlers?: {
      EduverseBridge?: {
        postMessage: (message: unknown) => void;
      };
    };
  };
}

export class EduToolBridge {
  private readonly context: BridgeContext;
  private readonly manifest: ToolManifest;
  private readonly handlers = new Map<BridgeMessageType, Set<BridgeMessageHandler>>();
  private initialized = false;
  private initPayload: InitPayload | null = null;
  /** Origin of the first EDUTOOL_INIT postMessage — trust-on-first-use guard. */
  private trustedOrigin: string | null = null;

  // Default values used when running standalone (no host).
  // La lingua NON sta qui: è per-tool e vive nel manifest (`locale[0]`).
  // Scriverla in questo file — che è INVARIANTE e viene clonato — produrrebbe
  // un tool che parla la lingua dell'originale. Vedi `defaultLocale()`.
  private static readonly STANDALONE_DEFAULTS: Omit<InitPayload, 'locale'> = {
    role: 'docente',
    theme: 'dark',
  };

  constructor(manifest: ToolManifest) {
    this.manifest = manifest;
    this.context = detectContext();
  }

  getContext(): BridgeContext {
    return this.context;
  }

  getManifest(): ToolManifest {
    return this.manifest;
  }

  getInitPayload(): InitPayload | null {
    return this.initPayload;
  }

  /**
   * Initialize the bridge:
   *   - Sets up incoming message listeners
   *   - Sends EDUTOOL_LOADED with the manifest
   *   - Waits for EDUTOOL_INIT from the host (or resolves with defaults if standalone)
   *   - Sends EDUTOOL_READY once init is complete
   */
  async init(): Promise<InitPayload> {
    if (this.initialized) {
      if (!this.initPayload) throw new Error('Bridge already initialized but no init payload');
      return this.initPayload;
    }

    this.setupIncomingListeners();

    // Standalone: read from URL query params, skip host messaging
    if (this.context === 'standalone') {
      this.initPayload = this.parseStandaloneParams();
      this.initialized = true;
      return this.initPayload;
    }

    // Host-attached: send LOADED and wait for INIT
    const initPromise = this.waitForInit();
    this.send('EDUTOOL_LOADED', { manifest: this.manifest });

    // Safety timeout: if host doesn't respond in 10s, fall back to defaults
    const timeoutPromise = new Promise<InitPayload>((resolve) => {
      setTimeout(
        () => resolve({ ...EduToolBridge.STANDALONE_DEFAULTS, locale: this.defaultLocale() }),
        10_000,
      );
    });

    this.initPayload = await Promise.race([initPromise, timeoutPromise]);
    this.initialized = true;

    // Announce readiness
    this.send('EDUTOOL_READY', { manifest: this.manifest });

    return this.initPayload;
  }

  /**
   * Subscribe to incoming messages of a specific type.
   * Returns an unsubscribe function.
   */
  on(type: BridgeMessageType, handler: BridgeMessageHandler): BridgeUnsubscribe {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(handler);
    return () => {
      this.handlers.get(type)?.delete(handler);
    };
  }

  /**
   * Send a message to the host (if any).
   * In standalone context this is a no-op.
   */
  send(type: BridgeMessageType, payload: unknown = {}): void {
    const message: BridgeMessage = {
      type,
      payload,
      timestamp: Date.now(),
    };

    switch (this.context) {
      case 'kmp-webview':
        this.sendToKmp(message);
        break;
      case 'eduverse-iframe':
        this.sendToIframe(message);
        break;
      case 'standalone':
        // Nothing to do — log for debugging
        if (import.meta.env.DEV) {
          console.debug('[Bridge] standalone send (no-op):', message);
        }
        break;
    }
  }

  /**
   * Request a fresh auth token from the host.
   * Returns a promise that resolves when EDUTOOL_AUTH_UPDATE arrives.
   */
  async requestAuthToken(): Promise<string | null> {
    if (this.context === 'standalone') return null;

    return new Promise((resolve) => {
      const unsubscribe = this.on('EDUTOOL_AUTH_UPDATE', (msg) => {
        unsubscribe();
        const payload = msg.payload as { authToken?: string };
        resolve(payload?.authToken ?? null);
      });

      this.send('EDUTOOL_REQUEST_AUTH', {});

      // Timeout after 5s
      setTimeout(() => {
        unsubscribe();
        resolve(null);
      }, 5_000);
    });
  }

  /**
   * Report asset loading progress to the host.
   * Used by the splash screen to display native progress bar.
   */
  reportAssetProgress(loaded: number, total: number): void {
    const percent = total > 0 ? (loaded / total) * 100 : 0;
    this.send('EDUTOOL_ASSET_PROGRESS', { loaded, total, percent });
  }

  /* ============================================================
   * Private implementation
   * ============================================================ */

  private setupIncomingListeners(): void {
    // Custom event: used by KMP Android to inject messages into the page
    window.addEventListener('eduverse-message', ((event: Event) => {
      const customEvent = event as CustomEvent<BridgeMessage>;
      if (customEvent.detail) this.dispatch(customEvent.detail);
    }) as EventListener);

    // postMessage: used by Eduverse iframe parent
    // Origin guard: trust the origin of the first EDUTOOL_INIT, reject others.
    window.addEventListener('message', (event: MessageEvent) => {
      const data = event.data as BridgeMessage | undefined;
      if (!this.isValidMessage(data)) return;

      if (this.trustedOrigin === null) {
        // First valid message — lock to this origin
        this.trustedOrigin = event.origin;
      } else if (event.origin !== this.trustedOrigin) {
        if (import.meta.env.DEV) {
          console.warn(`[bridge] Rejected message from untrusted origin: ${event.origin}`);
        }
        return;
      }

      this.dispatch(data);
    });
  }

  private async waitForInit(): Promise<InitPayload> {
    return new Promise((resolve) => {
      const unsubscribe = this.on('EDUTOOL_INIT', (msg) => {
        unsubscribe();
        resolve(msg.payload as InitPayload);
      });
    });
  }

  private sendToKmp(message: BridgeMessage): void {
    const w = window as WindowWithKmp;
    const json = JSON.stringify(message);

    // Android JavascriptInterface
    if (w.EduverseBridge?.postMessage) {
      w.EduverseBridge.postMessage(json);
      return;
    }

    // iOS WKScriptMessageHandler
    if (w.webkit?.messageHandlers?.EduverseBridge) {
      w.webkit.messageHandlers.EduverseBridge.postMessage(json);
      return;
    }
  }

  private sendToIframe(message: BridgeMessage): void {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }

  private dispatch(message: BridgeMessage): void {
    const handlers = this.handlers.get(message.type);
    if (!handlers) return;
    handlers.forEach((h) => {
      try {
        h(message);
      } catch (err) {
        console.error(`[Bridge] handler error for ${message.type}:`, err);
      }
    });
  }

  private isValidMessage(data: unknown): data is BridgeMessage {
    return (
      typeof data === 'object' &&
      data !== null &&
      'type' in data &&
      typeof (data as { type: unknown }).type === 'string' &&
      (data as { type: string }).type.startsWith('EDUTOOL_')
    );
  }

  private parseStandaloneParams(): InitPayload {
    const params = new URLSearchParams(window.location.search);

    const role = (params.get('role') ?? 'docente') as UserRole;
    const locale = (params.get('locale') ?? this.defaultLocale()) as Locale;
    const theme = (params.get('theme') ?? 'dark') as Theme;

    const payload: InitPayload = {
      role: this.validRole(role),
      locale: this.validLocale(locale),
      theme: this.validTheme(theme),
    };

    const target = params.get('target');
    if (target) {
      payload.context = { action: 'open', target };
    }

    return payload;
  }

  private validRole(role: string): UserRole {
    return ['docente', 'studente', 'display'].includes(role) ? (role as UserRole) : 'docente';
  }

  /** Lingua primaria del tool = prima voce di `manifest.locale`. */
  private defaultLocale(): Locale {
    return (this.manifest.locale?.[0] ?? 'it') as Locale;
  }

  private validLocale(locale: string): Locale {
    const supported = this.manifest.locale ?? [];
    return supported.includes(locale as Locale) ? (locale as Locale) : this.defaultLocale();
  }

  private validTheme(theme: string): Theme {
    return ['dark', 'light'].includes(theme) ? (theme as Theme) : 'dark';
  }
}

/**
 * Apply safe area insets to the document root as CSS variables.
 * Called by AppShell after receiving EDUTOOL_INIT.
 */
export function applySafeArea(insets: SafeAreaInsets): void {
  const root = document.documentElement;
  root.style.setProperty('--safe-top', `${insets.top}px`);
  root.style.setProperty('--safe-right', `${insets.right}px`);
  root.style.setProperty('--safe-bottom', `${insets.bottom}px`);
  root.style.setProperty('--safe-left', `${insets.left}px`);
}
